import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { useRouteParams } from '@/contexts/RouteContext';

import { useEditPageHeader } from '@/hooks/useEditPageHeader';
import { useGo } from '@/hooks/useResourceNavigation';

import { UserForm, type UserFormValues, type RoleOption } from './user-form';
import { USER_QUERY } from './users.gql.tsx';

interface UserRoleRecord {
	roleId?: number | null;
	role?: {
		roleId: number;
		name?: string | null;
		description?: string | null;
	} | null;
}

interface UserRecord {
	userId: string;
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	username?: string | null;
	isActive: boolean;
	userRoles?: UserRoleRecord[] | null;
}

interface UserQueryResponse {
	user?: UserRecord | null;
}

const ULID_REGEX = /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/u;

export const UserEdit = () => {
	const { id } = useRouteParams();

	const go = useGo();

	const navigateToList = () => go({ to: '/users' });
	useEditPageHeader({ id, resourceName: 'users' });

	const form = useForm<UserFormValues>({
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
			username: '',
			isActive: true,
			password: '',
			confirmPassword: '',
			roleIds: []
		}
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submissionNotice, setSubmissionNotice] = useState<string | null>(
		null
	);

	const isValidUlid = useMemo(() => {
		return typeof id === 'string' && ULID_REGEX.test(id.toUpperCase());
	}, [id]);

	const { data, loading, error, refetch } = useQuery<UserQueryResponse>(
		USER_QUERY,
		{
			variables: { id },
			skip: !id || !isValidUlid
		}
	);

	const userRecord = useMemo<UserRecord | null>(() => {
		return data?.user ?? null;
	}, [data?.user]);
	useEffect(() => {
		if (!userRecord) {
			return;
		}

		form.reset({
			firstName: userRecord.firstName ?? '',
			lastName: userRecord.lastName ?? '',
			email: userRecord.email ?? '',
			username: userRecord.username ?? '',
			isActive: userRecord.isActive,
			password: '',
			confirmPassword: '',
			roleIds:
				userRecord.userRoles
					?.map(role =>
						role?.roleId != null
							? String(role.roleId)
							: role?.role?.roleId != null
								? String(role.role.roleId)
								: undefined
					)
					.filter(
						(value): value is string => typeof value === 'string'
					) ?? []
		});
	}, [form, userRecord]);
	const roleOptions = useMemo<RoleOption[]>(() => {
		if (!userRecord?.userRoles) {
			return [];
		}

		return userRecord.userRoles
			.map(role => {
				const identifier = role?.roleId ?? role?.role?.roleId ?? null;
				const name = role?.role?.name?.trim();

				if (!identifier) {
					return null;
				}

				return {
					value: String(identifier),
					label: name || `Role #${identifier}`,
					description: role?.role?.description ?? null
				};
			})
			.filter((option): option is NonNullable<typeof option> =>
				Boolean(option)
			);
	}, [userRecord?.userRoles]);

	const handleSubmit = async (values: UserFormValues) => {
		setSubmissionNotice(null);
		setIsSubmitting(true);

		try {
			// User mutations are not yet available in the GraphQL schema.
			// eslint-disable-next-line no-console
			console.info('Captured user update payload', {
				id,
				...values
			});

			setSubmissionNotice(
				'User updates are not yet connected to the GraphQL API. The captured values have been logged for integration testing.'
			);

			// TODO: When user update mutation is implemented, navigate with selection state:
			// go({
			//   to: '/users',
			//   options: {
			//     state: { selectUserId: id }
			//   }
			// });
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!id) {
		return (
			<FormPageLayout>
				<Alert variant="destructive">
					<AlertTitle>Invalid route</AlertTitle>
					<AlertDescription>
						The user identifier is missing from the URL.
					</AlertDescription>
				</Alert>
				<div className="mt-4">
					<Button variant="outline" onClick={navigateToList}>
						Back to users
					</Button>
				</div>
			</FormPageLayout>
		);
	}

	if (!isValidUlid) {
		return (
			<FormPageLayout>
				<Card className="space-y-3">
					<h2 className="text-lg font-semibold text-destructive">
						Invalid user identifier
					</h2>
					<p className="text-sm text-muted-foreground">
						The provided user id{' '}
						<code className="px-2 py-1 rounded bg-muted">{id}</code>{' '}
						is not a valid ULID value.
					</p>
					<Button variant="outline" onClick={navigateToList}>
						Back to users
					</Button>
				</Card>
			</FormPageLayout>
		);
	}

	if (loading) {
		return (
			<FormPageLayout>
				<Card className="space-y-2">
					<p className="text-sm text-muted-foreground">
						Loading user details...
					</p>
				</Card>
			</FormPageLayout>
		);
	}

	if (error) {
		return (
			<FormPageLayout>
				<Alert variant="destructive">
					<AlertTitle>Unable to load user</AlertTitle>
					<AlertDescription>
						{error.message ||
							'The requested user could not be retrieved.'}
					</AlertDescription>
				</Alert>
				<div className="flex gap-2 mt-4">
					<Button variant="outline" onClick={() => void refetch()}>
						Retry
					</Button>
					<Button variant="ghost" onClick={navigateToList}>
						Back to users
					</Button>
				</div>
			</FormPageLayout>
		);
	}

	if (!userRecord) {
		return (
			<FormPageLayout>
				<Card className="space-y-2">
					<h2 className="text-lg font-semibold">User not found</h2>
					<p className="text-sm text-muted-foreground">
						The requested user could not be found or may have been
						removed.
					</p>
					<Button variant="outline" onClick={navigateToList}>
						Back to users
					</Button>
				</Card>
			</FormPageLayout>
		);
	}

	return (
		<FormPageLayout>
			{submissionNotice && (
				<Alert>
					<AlertTitle>Action required</AlertTitle>
					<AlertDescription>{submissionNotice}</AlertDescription>
				</Alert>
			)}

			<UserForm
				mode="edit"
				form={form}
				roleOptions={roleOptions}
				isSubmitting={isSubmitting}
				onSubmit={handleSubmit}
				headerTitle="Edit user"
			/>
		</FormPageLayout>
	);
};
